import _ from 'underscore';
import CloudFamily from './cloud-family';
import AtlUrl from './atl-url';
import AtlStorage from './atl-storage';
import HamletProducts from './hamlet-products';

export default class CartService {
  utmSource: string | null;
  hamletProducts: HamletProducts;
  storage: AtlStorage;
  accountName: string;
  orderID: string;
  items: any[];
  deprecatedItems: any[];

  constructor() {
    this.utmSource = AtlUrl.getParameter('utm_source');
    this.hamletProducts = new HamletProducts();
    this.storage = new AtlStorage();

    this.accountName    = '';
    this.orderID        = '';
    this.items          = [];
    this.deprecatedItems= [];
  }

  init() {

    const cloudProductFamily = new CloudFamily();
    const productKeys = cloudProductFamily.getProductKeys();
    const familyKeys = cloudProductFamily.getFamiliesPrimaryKey();
    const deprecatedProductKeys = cloudProductFamily.getDeprecatedProductKeys();

    let urlProducts = this.getAllURLProducts(familyKeys.jira, familyKeys.confluence);
    const hamletQueryKeys = _.uniq(_.keys(urlProducts).concat(productKeys));

    const hamletQueryOptions = {
      products: hamletQueryKeys,
      licenseTypes: ['evaluation'],
      monthsValid: 1
    };

    return this.hamletProducts.queryProducts(hamletQueryOptions)
      .then( () => {

        if (_.keys(urlProducts).length > 0) {
          urlProducts = _.omit(urlProducts, deprecatedProductKeys);

          _.each(urlProducts, (unitCount, productKey) => {

            const product = this.hamletProducts.findByKey(productKey);
            let orderable = null;

            if (product) {
              if (product.parentKey === 'atlassian.ondemand' || product.productType === 'HOSTED_ADDON' || unitCount !== 0) {
                orderable = _.where(product.orderableItems, { unitCount: unitCount })[0];
              }
              if (!orderable) {
                orderable = product.monthly.length > 0 ? product.monthly[0] : product.orderableItems[0];
              }
            }

            if (product && orderable) {
              const tier = {
                ...orderable,
                amount: orderable.renewalAmount,
                eula: product.contentData.eula,
                mpac: (product.marketplaceAddon === true)
              };
              this.items.push(tier);
            }
          });

        }

        return Promise.resolve(this.items);
      })
      .catch( e => {
        console.error(e);
        return Promise.reject();
      });
  }

  getItems() {
    return this.items;
  }

  setItems(itemList: any[]) {
    this.items = itemList;
  }

  getUtmSource() {
    return this.utmSource;
  }

  getAllURLProducts(jiraKey: string, confluenceKey: string) {

    let products = {};
    const DEFAULT_PRODUCT_COUNT = '10';

    let queryProducts = AtlUrl.getParameter('product');
    if (!queryProducts) {
      queryProducts = AtlUrl.getParameter('products') || '';
    }
    queryProducts = queryProducts.split(',');

    // if JSM in-url, change to JSD productKey
    queryProducts = queryProducts.map(function(product) {
      return product === 'jira-service-management.ondemand'
        ? 'jira-servicedesk.ondemand' : product;
    });

    let unitCounts = AtlUrl.getParameter('unitCount') || DEFAULT_PRODUCT_COUNT;
    unitCounts = unitCounts.split(',').map( count => parseInt(count, 10) );

    for (let i = 0; i < queryProducts.length; i++) {
      let key = queryProducts[i];
      let count = 10;

      if (unitCounts.length === 1) {
        if (key === jiraKey || key === confluenceKey) {
          count = unitCounts[0];
        }
      } else if (unitCounts.length === queryProducts.length) {
        count = unitCounts[i];
      }

      if (key) {
        products[key] = count;
      }
    }

    return products;
  }

  /*
   * Whether you have any MARKETPLACE addons in your cart
   */
  MPACAddons() {
    return _.where(this.items, { mpac: true }).length > 0;
  }

  /*
   * A list of products in your basket that have role=based pricing
   */
  roleBasedProducts() {
    return _.where(this.items, { unitBlockSize: 1 });
  }

  /*
   * Whether you have any addons in your cart
   */
  addons() {
    return _.where(this.items, { productType: 'HOSTED_ADDON' }).length > 0;
  }

  /*
   * Which addons in your cart
   */
  getAddons() {
    return _.where(this.items, { productType: 'HOSTED_ADDON' });
  }

  /*
   * Which core products in your cart
   */
  getCoreProducts() {
    return _.filter(this.items, function(item) {
      return item.productType !== 'HOSTED_ADDON';
    });
  }

  /**
   * Returns array of deprecated products attempted to be added to the cart
   * @returns {Array}
   */
  getDeprecatedProductsInCart() {
    return this.deprecatedItems;
  }

  /*
   * Returns an array of product keys for all items in the cart
   */
  productsInCart() {
    return _.reduce(this.items, function (memo, item) {
      memo.push(item.productKey);
      return memo;
    }, []);
  }

  /*
   * Get a list of cart items that have third-part eulas
   */
  getEulas() {
    return _.filter(this.items, function (item) {
      return _.has(item, 'eula') && !!item.eula;
    });
  }

  /*
   * Wipe out everything in the ondemand form fields and in local storage. Complete data reset
   * for completed and successful orders after they're done.
   */
  clearFields() {
    this.storage.session.clear();
    this.storage.cookie.remove('ONDEMANDCART');
    this.items = [];
    this.utmSource = null;
  }

}
